<template>
    <section class="mx-2 my-6" :aria-labelledby="reviewTitleId">
        <BaseCard theme="default" padding="none" radius="lgr" elevated class="overflow-hidden">
            <div aria-label="Development goal details" class="px-5 pt-5 pb-6 sm:px-7 sm:pt-6 sm:pb-7">
                <FeatureChip feature="journeys">New Journey</FeatureChip>
                <h2 :id="reviewTitleId" class="display-small-serif leading-7 mt-4 text-ink">
                    {{ goal.title }}
                </h2>
                <p class="mt-3 body text-valence-stone-500">{{ goal.achievement_criteria }}</p>
                <p v-if="isSuperseded" :id="supersededDescriptionId" class="body-small mt-3 text-ink-muted">A newer draft is available below.</p>
            </div>
            <div class="flex shrink-0 flex-col gap-3 border-t border-border bg-surface-sunken px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
                <p v-if="goal.target_date" class="scaffold-body-small leading-5 text-ink-muted">
                    Target date: <time :datetime="goal.target_date">{{ formattedTargetDate }}</time>
                </p>
                <BaseButton
                    theme="primary"
                    class="base-btn-dark w-full shrink-0 justify-center sm:ml-auto sm:w-auto"
                    :disabled="!canSave || isSaving || isSaved"
                    :aria-describedby="isSuperseded ? supersededDescriptionId : undefined"
                    @click="emit('save')">
                    {{ isSaving ? "Saving goal…" : isSaved ? "Goal saved" : actionLabel }}
                </BaseButton>
            </div>
        </BaseCard>
    </section>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import FeatureChip from "~vue/components/FeatureChip.vue";
import type { DevelopmentGoalDraft } from "~vue/types/chat";
import { DateTime } from "luxon";
import { computed, useId } from "vue";

const props = withDefaults(defineProps<{ goal: DevelopmentGoalDraft; actionLabel?: string; canSave?: boolean; isSuperseded?: boolean; isSaving?: boolean; isSaved?: boolean }>(), {
    actionLabel: "Save this goal",
    canSave: true,
    isSuperseded: false,
    isSaving: false,
    isSaved: false,
});

const emit = defineEmits<{ save: [] }>();
const reviewTitleId = useId();
const supersededDescriptionId = useId();

const formattedTargetDate = computed(() => {
    if (!props.goal.target_date) {
        return "";
    }

    const targetDate = DateTime.fromISO(props.goal.target_date, { zone: "utc" });

    return targetDate.isValid ? targetDate.toFormat("LLLL d, yyyy") : props.goal.target_date;
});
</script>
